import { Mui, MuiIcons } from '@osu/react-ui';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { STATUSES, TOOLTIPS } from '../../util/constants';
import { filterAndSortInProgress } from "../transform";
import { PullCard } from './pull-card';
import useRoleAuthentication from "../../Common/hooks/useRoleAuthentication";
import {
  useGetCommitteeListQuery,
  useGetInProgressQuery,
  useGetNextAppQuery,
  graphqlApi,
} from '../../services/graphql/api';

const styles = {
  table: { minWidth: '100%' },
  Grid: { margin: '0 0 2rem 0', display: 'flex', flexWrap: 'wrap', gap: '1rem' },
  fixTopHat: { '&::before': { display: 'none' } },
  box: (theme) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  }),
};

// Sorting helpers for tables
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilized = array.map((el, idx) => [el, idx]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map(el => el[0]);
}

export const Dashboard = () => {
  const authDetails = useRoleAuthentication();
  const user = useSelector(state => state?.authentication?.user);
  const dispatch = useDispatch();

  // ── RTK Query hooks ──
  const skipQueries = authDetails.READ_ACCESS !== true;
  const {
    data: committeeData,
    isLoading: committeeLoading,
  } = useGetCommitteeListQuery(undefined, { skip: skipQueries, refetchOnMountOrArgChange: true });

  const {
    data: progressResult,
    isLoading: progressLoading,
    isError: isProgressError,
  } = useGetInProgressQuery(undefined, { skip: skipQueries, refetchOnMountOrArgChange: true });

  // Log progress query errors for debugging
  if (isProgressError) {
    console.error('Dashboard: getInProgress query failed');
  }

  // nextApp: only fetch when a committeeId is requested
  const [nextAppCommitteeId, setNextAppCommitteeId] = useState(null);
  const {
    data: nextAppData,
    isError: isNextAppError,
    isFetching: isNextAppFetching,
    error: nextAppQueryError,
  } = useGetNextAppQuery(nextAppCommitteeId, { skip: !nextAppCommitteeId, refetchOnMountOrArgChange: true });

  // Derive data from RTK Query results
  const committeesData = committeeData?.committees;
  const progressData = progressResult?.inProgress;
  const dashInprogress = progressResult?.inProgress?.dashInprogress;
  const assignedData = progressResult?.assigned;
  const pastDueData = progressResult?.pastDue;
  const trainingData = progressResult?.trainings;

  function Alert(props) {
    return <Mui.Alert elevation={6} variant="filled" {...props} />;
  }

  let navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [nextAppError, setNextAppError] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [inProgressViewFilter, setInProgressViewFilter] = useState(["saved", "assigned"]);
  const [trainingViewFilter, setTrainingViewFilter] = useState(["assigned"]);
  const [inProgressData, setInProgressData] = useState([]);
  const [trainingLocalData, setTrainingLocalData] = useState([]);
  const [inProgressOrder, setInProgressOrder] = useState('asc');
  const [inProgressOrderBy, setInProgressOrderBy] = useState('createdDatetime');
  const [trainingOrder, setTrainingOrder] = useState('asc');
  const [trainingOrderBy, setTrainingOrderBy] = useState('createdDatetime');

  // Sorted data
  const sortedInProgressData = stableSort(inProgressData, getComparator(inProgressOrder, inProgressOrderBy));
  const sortedTrainingData = stableSort(trainingLocalData, getComparator(trainingOrder, trainingOrderBy));

    // Table head cells configuration
  const inProgressHeadCells = [
    { id: 'applicantEmployeeId', label: 'OSUID' },
    { id: 'lastName', label: 'Last Name' },
    { id: 'firstName', label: 'First Name' },
    { id: 'committeeName', label: 'Committee' },
    { id: 'createdDatetime', label: 'Assigned Date' },
    { id: 'completedByDueDate', label: 'Due Date' },
  ];
  const trainingHeadCells = [
    { id: 'applicantEmployeeId', label: 'OSUID' },
    { id: 'lastName', label: 'Last Name' },
    { id: 'firstName', label: 'First Name' },
    { id: 'committeeName', label: 'Committee' },
    { id: 'createdDatetime', label: 'Assigned Date' },
    { id: 'status', label: 'Status' },
  ];

  // snackbar controls
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  // updateNextApp replacement — sets committeeId to trigger the RTK Query hook
  const updateNextApp = (committeeId) => {
    dispatch(graphqlApi.util.invalidateTags([{ type: 'NextApp', id: committeeId }]));
    setNextAppCommitteeId(committeeId);
    setDisableButton(true);
  };

  useEffect(() => {
    // clear nextApp state on location change (replaces cleanup())
    setNextAppCommitteeId(null);
    setNextAppError(false);
  }, [location.pathname]);

  useEffect(() => {
    const filteredData = filterAndSortInProgress(inProgressViewFilter, progressData?.saved, progressData?.assigned, progressData?.notifyAdmin );
    setInProgressData(filteredData);
  }, [progressData, inProgressViewFilter]);
  
  useEffect(() => {
    if(!trainingData || trainingData.length === 0) return;
    let filtered = [];
    if(trainingViewFilter.length > 0) {
       if(trainingViewFilter.includes("assigned")) {
        filtered = [...filtered, ...trainingData?.filter(e => e.status === "Assigned")];
      } if(trainingViewFilter.includes("complete")) {
        filtered = [...filtered, ...trainingData?.filter(e => e.status === "Complete")];
      } else {
        setTrainingLocalData(trainingData);
      }
      setTrainingLocalData(filtered);
    } else {
      setTrainingLocalData(trainingData);
    }
  }, [trainingData, trainingViewFilter]);

  // Navigate on successful nextApp fetch
  useEffect(() => {
    if(!isNextAppFetching && nextAppData?.status === "success") {
        navigate(`/applicant/${nextAppData?.applicantEmployeeId}/${nextAppData?.committeeId}/${nextAppData?.term}`);
        setNextAppCommitteeId(null);
        setDisableButton(false);
    }
  }, [nextAppData?.applicantEmployeeId, nextAppData?.committeeId, nextAppData?.status, nextAppData?.term, isNextAppFetching, navigate]);

  useEffect(() => {
    if(!isNextAppFetching && nextAppData?.status === "failed") {
      setNextAppError(nextAppData?.error?.message ?? "There was an error retrieving the next application");
      setOpen(true);
      setDisableButton(false);
    }
    if(isNextAppError) {
      setNextAppError(nextAppQueryError?.message ?? "There was an error retrieving the next application");
      setOpen(true);
      setDisableButton(false);
    }
  }, [nextAppData?.error?.message, nextAppData?.status, isNextAppError, isNextAppFetching, nextAppQueryError]);

  if (nextAppError) {
    console.log(nextAppError);
  }

  return (
    <Mui.Box style={{ display: 'flex', flexDirection: 'column' }}>
      <Mui.Box sx={styles.Grid}>
        {(committeeLoading || !committeesData || (!progressResult && !isProgressError)) ? (
          <Mui.Box style={{display:'flex', justifyContents: 'flex-evenly', height: '10rem', width: '100%'}}>
            <Mui.Skeleton variant="rectangular" height='100%' width='100%'/>
          </Mui.Box>
        ) : (
          <>
          <Mui.Grid container sx={{flexGrow: 1}} spacing={2}>
            {committeesData?.map((committee) => 
              (
              <Mui.Grid key={committee?.committeeId ?? committee?.committeeName} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                <PullCard
                  committee={committee}
                  inProgressLength={progressData?.saved?.filter(e => e.manuallyAssigned !== true || e.adminAction !== "NOTIFY")?.length ?? 0}
                  updateNextApp={updateNextApp}
                  disableButton={disableButton}
                  setDisableButton={setDisableButton}
                  reviewCountNew={assignedData?.find(e => e.committeeId === committee.committeeId)?.count ?? 0}
                  reviewCountInProgress={dashInprogress?.filter(e => e.committeeId === committee.committeeId)?.length ?? 0}
                  cardLink="#"
                  completed={(assignedData?.find(e => e.committeeId === committee.committeeId)?.count ?? -1) === 0}
                  pastDue={pastDueData?.find(e => e.committeeId === committee.committeeId)?.count ?? 0}
                  groups={user?.cognitoGroups}
                />
              </Mui.Grid>
            ))}
            </Mui.Grid>
          </>
        )} 
      <Mui.Snackbar open={open} onClose={handleClose} anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}>
        <Alert onClose={handleClose} severity="info">
          {nextAppError ?? "There was an error retrieving the next application."}    
        </Alert>
      </Mui.Snackbar>
      </Mui.Box>
      <Mui.Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Mui.Accordion defaultExpanded={true}>
          <Mui.AccordionSummary
            expandIcon={<MuiIcons.ExpandMore />}
            id={'1'}
            heading={'test'}
            style={{ fontSize: '1rem' }}
            sx={styles.accordion}
          >
            <Mui.Typography
              aria-label="In Progress"
              style={{ paddingLeft: '1rem' }}
            >
              In Progress
            </Mui.Typography>
          </Mui.AccordionSummary>
          <Mui.AccordionDetails>
          <Mui.Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1
            }}
          >
          <Mui.ToggleButtonGroup
            value={inProgressViewFilter}
            onChange={(e, ev) => { setInProgressViewFilter(ev)}}
            aria-label="In progress view filter buttons"
          >
            <Mui.ToggleButton value="assigned" aria-label="assigned" style={{ margin: '1rem 0' }}>
                Manually Assigned ({progressData?.assigned?.length ?? 0})
              </Mui.ToggleButton>
              <Mui.ToggleButton value="saved" aria-label="saved for later" style={{ margin: '1rem 0' }}>
                Saved for later ({progressData?.saved?.length ?? 0})
              </Mui.ToggleButton>
              <Mui.ToggleButton value="admin" aria-label="admin" style={{ margin: '1rem 0' }}>
                Notified Admin ({progressData?.notifyAdmin?.length ?? 0})
              </Mui.ToggleButton>
            </Mui.ToggleButtonGroup>
            <br />
            <Mui.TableContainer>
              <Mui.Table
                sx={styles.table}
                aria-label="in progress list"
              >
                <Mui.TableHead>
                    <Mui.TableRow>
                      {inProgressHeadCells.map((headCell) => (
                        <Mui.TableCell
                          key={headCell.id}
                          sortDirection={inProgressOrderBy === headCell.id ? inProgressOrder : false}
                        >
                          <Mui.TableSortLabel
                            active={inProgressOrderBy === headCell.id}
                            direction={inProgressOrderBy === headCell.id ? inProgressOrder : 'asc'}
                            onClick={() => {
                              const isAsc = inProgressOrderBy === headCell.id && inProgressOrder === 'asc';
                              setInProgressOrder(isAsc ? 'desc' : 'asc');
                              setInProgressOrderBy(headCell.id);
                            }}
                          >
                            {headCell.label}
                          </Mui.TableSortLabel>
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {progressLoading ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={6} component="th" scope="row">
                        <Mui.Skeleton variant="rectangular" width="100%" height="2rem" />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : (
                    sortedInProgressData?.map((row) => (
                      (<Mui.TableRow
                        key={`${row.applicantEmployeeId}-${row.committeeId}-${row.term}`}
                      >
                        <Mui.TableCell component="th" scope="row">
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Mui.Link
                              component={Link}
                              to={`/applicant/${row.applicantEmployeeId}/${row.committeeId}/${row.term}`}
                            > {row.applicantEmployeeId} </Mui.Link>
                              { row?.adminAction === STATUSES.NOTIFY &&
                                TOOLTIPS('NOTIFY')
                              }
                              {row?.status === STATUSES.RESUMELATER && row?.adminAction !== STATUSES.NOTIFY &&
                                TOOLTIPS('RESUMELATER')}
                              { new Date(row?.completedByDueDate) < new Date() && row?.adminAction !== STATUSES.NOTIFY &&
                                TOOLTIPS('PASTDUE')
                              }
                              {row?.manuallyAssigned &&
                                TOOLTIPS('ASSIGNED')
                              }
                              {row?.notificationCleared &&
                                TOOLTIPS('NOTIFY_CLEARED')
                              }
                              {row?.training && 
                                TOOLTIPS('TRAINING')
                              }
                            </div>
                        </Mui.TableCell>
                        <Mui.TableCell>{row?.lastName}</Mui.TableCell>
                        <Mui.TableCell>{row?.firstName}</Mui.TableCell>
                        <Mui.TableCell>
                          {row?.committeeName}
                        </Mui.TableCell>
                        <Mui.TableCell>{ new Date(row?.createdDatetime).toLocaleDateString() }</Mui.TableCell>
                        <Mui.TableCell>{ new Date(row?.completedByDueDate).toLocaleDateString() } </Mui.TableCell>
                      </Mui.TableRow>)
                    ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
              {!progressLoading && inProgressData?.length <= 0 && (
                <Mui.Box sx={styles.box}>
                  <Mui.Typography>
                    There are no applications to display.
                  </Mui.Typography>
                </Mui.Box>
              )}
            </Mui.TableContainer>
          </Mui.Box>
          </Mui.AccordionDetails>
        </Mui.Accordion>
      </Mui.Box>
      <Mui.Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          marginTop: '2rem'
        }}
      >
        <Mui.Accordion defaultExpanded={true}>
          <Mui.AccordionSummary
            expandIcon={<MuiIcons.ExpandMore />}
            style={{ fontSize: '1rem' }}
            sx={styles.accordion}
          >
            <Mui.Typography
              aria-label="Training Items"
              style={{ paddingLeft: '1rem' }}
            >
              Training Items
            </Mui.Typography>
          </Mui.AccordionSummary>
          <Mui.AccordionDetails>
          <Mui.Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1
            }}
          >
          <Mui.ToggleButtonGroup
            value={trainingViewFilter}
            onChange={(e, ev) => { setTrainingViewFilter(ev)}}
            aria-label="training view filter buttons"
          >
              <Mui.ToggleButton value="assigned" aria-label="Assigned">
                Assigned ({trainingData?.filter(e => e.status === "Assigned")?.length ?? 0})
              </Mui.ToggleButton>
              <Mui.ToggleButton value="complete" aria-label="Complete">
                Complete ({trainingData?.filter(e => e.status === "Complete")?.length ?? 0})
              </Mui.ToggleButton>
             
            </Mui.ToggleButtonGroup>
            <Mui.TableContainer>
              <Mui.Table
                sx={styles.table}
                aria-label="training list"
              >
                <Mui.TableHead>
                   <Mui.TableRow>
                      {trainingHeadCells.map((headCell) => (
                        <Mui.TableCell
                          key={headCell.id}
                          sortDirection={trainingOrderBy === headCell.id ? trainingOrder : false}
                        >
                          <Mui.TableSortLabel
                            active={trainingOrderBy === headCell.id}
                            direction={trainingOrderBy === headCell.id ? trainingOrder : 'asc'}
                            onClick={() => {
                              const isAsc = trainingOrderBy === headCell.id && trainingOrder === 'asc';
                              setTrainingOrder(isAsc ? 'desc' : 'asc');
                              setTrainingOrderBy(headCell.id);
                            }}
                          >
                            {headCell.label}
                          </Mui.TableSortLabel>
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {progressLoading ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={6} component="th" scope="row">
                        <Mui.Skeleton variant="rectangular" width="100%" height="2rem" />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : (
                    sortedTrainingData?.map((row) => (
                      <Mui.TableRow
                        key={`${row.applicantEmployeeId}-${row.committeeId}-${row.term}`}
                      >
                        <Mui.TableCell component="th" scope="row">
                          <Mui.Link
                            component={Link}
                            to={`/training/${row.applicantEmployeeId}/${row.committeeId}/${row.term}`}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              {row.applicantEmployeeId}
                            </div>
                          </Mui.Link>
                        </Mui.TableCell>
                        <Mui.TableCell>{row?.lastName}</Mui.TableCell>
                        <Mui.TableCell>{row?.firstName}</Mui.TableCell>
                        <Mui.TableCell>
                          {row?.committeeName}
                        </Mui.TableCell>
                        <Mui.TableCell>{new Date(row?.createdDatetime).toLocaleDateString()}</Mui.TableCell>
                        <Mui.TableCell>
                          {row?.status}
                        </Mui.TableCell>
                      </Mui.TableRow>
                    ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
              {!progressLoading && trainingData?.length <= 0 && (
                <Mui.Box sx={styles.box}>
                  <Mui.Typography>
                    There are no training applications to display.
                  </Mui.Typography>
                </Mui.Box>
              )}
            </Mui.TableContainer>
          </Mui.Box>
          </Mui.AccordionDetails>
        </Mui.Accordion>
      </Mui.Box>
    </Mui.Box>
  );
};

export default Dashboard;
